// MasterParcoursEngineCore v1.0 --- PESACORE Architect

const getParcoursStore = () => (typeof window !== 'undefined' ? window.__PARCOURS_STORE__ : null);

// Configuration de l'URL du serveur
const API_URL = process.env.REACT_APP_API_URL || "https://localhost:5000";

export class MasterParcoursEngine {
  constructor(courseData, userProgress) {
    if (!courseData) throw new Error("Engine needs Course Data");
    
    this.course = courseData;
    const savedHarvest = userProgress?.harvestedData || {};
    this.harvestedData = {
      reflections:       savedHarvest.reflections       || {},
      extractedConcepts: savedHarvest.extractedConcepts || [],
      evidences:         savedHarvest.evidences          || [],
      steps:             savedHarvest.steps              || {},
    }; 
    this.state = this._bootstrapState(courseData, userProgress);
    this.history = [];
    
    this._ensureSetConsistency();
    console.log("Engine initialized for:", courseData.nom || courseData.title);
  }

  _resolveAssetUrl(path) {
  if (!path || typeof path !== 'string') return path;
  
  // 1. Si c'est déjà une URL absolue ou un blob (preview), on ne touche à rien
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }

  // 2. Si le chemin contient déjà l'API_URL (cas où l'engine repasse deux fois), on stoppe
  if (path.includes(API_URL)) return path;

  // 3. Nettoyage
  let cleanPath = path.trim().replace(/^\/+/, ''); 
  if (cleanPath.startsWith('uploads/')) {
    cleanPath = cleanPath.replace(/^uploads\//, '');
  }

  return `${API_URL}/uploads/${cleanPath}`;
  }

  _bootstrapState(parcours, progress) {
    const completionMap = {};
      if (progress?.moduleProgress) {
        progress.moduleProgress.forEach(mp => {
          if (mp.moduleId) {
            const key = mp.moduleId.toString();
            completionMap[key] = mp.overallProgress || 0;
          }
        });
      }
    const normalizedModules = parcours.modules.map((m, idx) => {
    // === 1. Identifiant robuste ===
      const getModuleId = () => {
        // 1a. Si un id direct a été fourni (ex: depuis le contrôleur modifié)
        if (m.id) return String(m.id);
        
        // 1b. Depuis la référence legacy moduleId
        const raw = m.moduleId?._id || m.moduleId?.id || m.moduleId;
        if (raw) {
          const str = raw?.$oid || (typeof raw === 'object' ? (raw._id || raw.id || raw).toString() : String(raw));
          return str === '[object Object]' ? `module-${idx}` : str;
        }
        
        // 1c. Fallback ultime pour le builder (module sans moduleId)
        return m.moduleId?._id?.toString() || m._id?.toString() || `module-${idx}`;
      };

      const finalId = getModuleId();

      // === 2. Fusion des propriétés (legacy vs builder) ===
      // legacy : données dans moduleId, builder : directement dans m
      const legacy = m.moduleId || {};
      const titre = m.titre || legacy.nom || "Module";
      const objectifs = m.objectifs?.length ? m.objectifs : (legacy.objectifs || []);
      const competences = m.competences?.length ? m.competences : (legacy.competences || []);
      const cases = m.cases || [];
      const diagnosticQuiz = m.diagnosticQuiz || null;
      const finalQuiz = m.finalQuiz || null;

      // Nettoyage des granules (Images & Vidéos)
      const sanitizedGranules = (m.granules || []).map(g => {
        // 1. Image principale du granule (si elle existe)
        const mainImage = this._resolveAssetUrl(g.image || g.thumbnail);

        // 2. Scan des images dans les validationSteps
        if (g.validationSteps) {
          g.validationSteps.forEach(step => {
            if (step.questions) {
              step.questions.forEach(q => {
                if (q.images && Array.isArray(q.images)) {
                  // On transforme chaque nom de fichier en URL complète
                  q.images = q.images.map(img => this._resolveAssetUrl(img));
                }
              });
            }
          });
        }

        // 3. Scan des fichiers (PDF, etc.)
        if (g.files) {
          g.files.forEach(f => {
            if (f.filePath) f.filePath = this._resolveAssetUrl(f.filePath);
          });
        }

        return {
          ...g,
          id: g.id?.$oid || g.id || g._id?.$oid ||  g._id,
          image: mainImage,
          associatedFicheId: g.content?.associatedFicheId || null,
          isHarvestable: g.content?.steps?.some(s => s.isHarvestable) || false
        };
      });

      return {
        id: finalId,
        title: titre,
        completion: completionMap[finalId] || 0,
        score: progress?.[finalId]?.score || 0,
        isLocked: parcours.engineConfig?.isSequencial ? (idx > 0 && !progress?.[finalId]?.unlocked) : false,
        status: idx === 0 ? 'active' : (progress?.[finalId]?.unlocked ? 'active' : 'locked'),
        granules: sanitizedGranules,
        fiches: m.fiches || [],
        bonusFiches: m.bonusFiches || [],
        objectifs,
        competences,
        tags: legacy.tags || [],
        engineConfig: parcours.engineConfig || {},
        cases,
        diagnosticQuiz,
        finalQuiz,
      };
    });

    let completedGranules = new Set();
    if (progress?.submissions) {
      // Pour chaque soumission, on cherche si elle correspond à un granule existant
      for (const sub of progress.submissions) {
        const subGranuleId = sub.granuleId?.toString();
        // Trouver le module de la soumission
        const module = normalizedModules.find(m => m.id === sub.moduleId?.toString());
        if (module) {
          // Chercher le granule dans ce module qui pourrait correspondre à la soumission
          const granule = module.granules.find(g => {
            // Tenter toutes les correspondances possibles d'ID
            const gId = g.id?.toString();
            const gIdObj = g._id?.toString(); // parfois _id est une string ou un objet
            return gId === subGranuleId || gIdObj === subGranuleId;
          });
          if (granule) {
            // Ajouter l'ID normalisé du granule
            completedGranules.add(granule.id.toString());
          }
          // Si pas trouvé, on ignore cette soumission (quiz, micro-éval, etc.)
        }
      }
    }

    if (parcours.capstone) {
      const capstoneGranule = {
        id: parcours.capstone.activityId?._id?.toString() || parcours.capstone.activityId?.toString() || 'capstone',
        type: 'capstone',
        titre: 'Capstone – Projet Final',
        duration: 2880, // ou parcours.capstone.duration
        order: 1,
        content: {
          ...parcours.capstone,
          // Enrichir avec les données de l'activité disponibles
          realTime: parcours.capstone.realTimeConfig || parcours.capstone.realTime,
          isCapstone: true
        },
        image: '/images/capstone-banner.jpg', // optionnel : une belle bannière
        isHarvestable: false
      };

      const capstoneModule = {
        id: 'capstone-final',
        title: 'Grande Finale Nationale',
        completion: progress?.capstoneProgress?.completion || 0,
        score: progress?.capstoneProgress?.score || 0,
        isLocked: parcours.engineConfig?.isSequencial 
          ? !progress?.capstoneUnlocked 
          : false,
        status: progress?.capstoneUnlocked ? 'active' : 'locked',
        granules: [capstoneGranule],
        fiches: [], // ou tu peux injecter toutes les fiches du parcours si tu veux
        isCapstone: true,
        engineConfig: parcours.engineConfig || {}
      };

      normalizedModules.push(capstoneModule);
    }

    // Calcul de l'index courant
    let currentModuleIndex = 0;
    if (progress?.currentLocation?.moduleId) {
      const savedId = progress.currentLocation.moduleId.toString();
      currentModuleIndex = normalizedModules.findIndex(m => m.id === savedId);
      if (currentModuleIndex === -1) currentModuleIndex = 0;
    }

    // Logique de déblocage séquentiel
    if (parcours.engineConfig?.isSequencial) {
      normalizedModules.forEach((mod, i) => {
        if (i === 0) mod.isLocked = false;
        // Si le précédent a un score > 70%, on débloque le suivant
        const prevMod = normalizedModules[i-1];
        if (prevMod && (progress?.[prevMod.id]?.score >= 70)) {
            mod.isLocked = false;
        }
      });
    }

    return {
      currentModuleIndex: this._calculateCurrentIndex(normalizedModules, progress),
      modules: normalizedModules,
      //completedGranules: new Set(progress?.submissions?.map(s => s.granuleId?.toString()) || []),
      completedGranules,
      pesaCount: progress?.stats?.pointsAccumules || 0,
      isCertified: progress?.isCertified || false,
      harvestedData: this.harvestedData // On injecte le buffer dans le state
    };
  }

  harvest(stepId, payload) {
    if (!this.harvestedData.steps) {
      this.harvestedData.steps = {};
    }
    this.harvestedData.steps[stepId] = {
      ...payload,
      harvestedAt: new Date().toISOString(),
    };
    console.log(`[ENGINE] 📥 Data Harvested for step ${stepId}:`, payload.type);
    this._notifyStore();
  }

  _ensureSetConsistency() {
    if (!(this.state.completedGranules instanceof Set)) {
      this.state.completedGranules = new Set(this.state.completedGranules);
    }
  }
  
  validateGranule(granuleId, results = {}) {
    const id = granuleId?.toString();

    if (results.harvestedData) {
      Object.entries(results.harvestedData).forEach(([key, payload]) => {
        // On stocke le payload riche (text, mediaBase64, mediaType, etc.)
        this.harvest(id, {
          stepKey: key,
          ...payload
        });
      });
    } 
    // Compatibilité avec l'ancien système de réflexion simple
    else if (results.reflection) {
      this.harvest(id, { type: 'text', content: results.reflection });
    }

    // 2. Progression
    if (this.state.completedGranules.has(id)) return;
    this.state.completedGranules.add(id);

    if (results.pesaGain) {
      this.state.pesaCount += results.pesaGain;
    }

    this._recalculateProgress();
  }

  serialize() {
    return {
      currentModuleIndex: this.state.currentModuleIndex,
      currentLocation: this.state.currentLocation || { moduleId: this.state.modules[this.state.currentModuleIndex]?.id },
      pesaCount: this.state.pesaCount,
      isCertified: this.state.isCertified,
      harvestedData: this.harvestedData,
      completedGranules: Array.from(this.state.completedGranules), // Convertit le Set en Array pour JSON
      // On envoie un condensé du progrès par module pour MongoDB
      moduleProgress: this.state.modules.map(m => ({
        moduleId: m.id,
        completion: m.completion,
        score: m.score,
        isLocked: m.isLocked
      }))
    };
  }

  _baseSerialize() {
    return {
      currentModuleIndex: this.state.currentModuleIndex,
      pesaCount: this.state.pesaCount,
      isCertified: this.state.isCertified,
      completedGranules: Array.from(this.state.completedGranules),
      moduleProgress: this.state.modules.map(m => ({
        moduleId: m.id,
        completion: m.completion,
        score: m.score,
        isLocked: m.isLocked
      }))
    };
  }

  _calculateCurrentIndex(modules, progress) {
    if (!progress?.currentLocation?.moduleId) return 0;

    const savedId = progress.currentLocation.moduleId.toString();
    const index = modules.findIndex(m => m.id === savedId);
    return index !== -1 ? index : 0;
  }

  _recalculateProgress() {
    const { modules, currentModuleIndex, completedGranules } = this.state;
    const { engineConfig = {}, capstone } = this.course;

    let hasUpdated = false;

    modules.forEach((module) => {
      if (!module.granules || module.granules.length === 0) {
        module.completion = 100;
        return;
      }

      const completedCount = module.granules.filter(g => completedGranules.has(g.id)).length;
      const newCompletion  = Math.round((completedCount / module.granules.length) * 100);
      if (module.completion !== newCompletion) {
        module.completion = newCompletion;
        hasUpdated = true;
      }
      module.score = module.completion;
    });

    if (engineConfig.isSequencial) {
      modules.forEach((module, idx) => {
        if (idx === 0) {
          if (module.isLocked) { module.isLocked = false; module.status = 'active'; hasUpdated = true; }
          return;
        }
        const prevModule    = modules[idx - 1];
        const shouldUnlock  = prevModule.completion >= 70;
        if (module.isLocked && shouldUnlock)  { module.isLocked = false; module.status = 'active'; hasUpdated = true; }
        else if (!module.isLocked && !shouldUnlock) { module.isLocked = true; module.status = 'locked'; hasUpdated = true; }
      });
    }

    const capstoneModuleIndex = modules.findIndex(m => m.isCapstone);
    if (capstoneModuleIndex !== -1) {
      const capstoneModule     = modules[capstoneModuleIndex];
      const allPreviousCompleted = modules.slice(0, capstoneModuleIndex).every(m => m.completion >= 70);
      if (capstoneModule.isLocked && allPreviousCompleted) {
        capstoneModule.isLocked = false; capstoneModule.status = 'active'; hasUpdated = true;
      }
      const capstoneGranuleId = capstone?.activityId || capstoneModule.granules[0]?.id;
      const isCapstoneDone    = capstoneGranuleId && completedGranules.has(capstoneGranuleId);
      if (isCapstoneDone && !this.state.isCertified) { this.state.isCertified = true; hasUpdated = true; }
    } else {
      const allModulesCompleted = modules.every(m => m.completion >= 70);
      if (allModulesCompleted && !this.state.isCertified) { this.state.isCertified = true; hasUpdated = true; }
    }

    const currentModule = modules[currentModuleIndex];
    if (currentModule && currentModule.isLocked && engineConfig.isSequencial) {
      const newIndex = modules.findIndex(m => !m.isLocked);
      if (newIndex !== -1 && newIndex !== currentModuleIndex) {
        this.state.currentModuleIndex = newIndex; hasUpdated = true;
      }
    }

    if (hasUpdated) this._notifyStore();
  }

  validateFinalQuiz(moduleId, passed = true) {
    if (!moduleId) return;

    const moduleIndex = this.state.modules.findIndex(m => m.id === moduleId);
    if (moduleIndex === -1) {
        console.warn("validateFinalQuiz : module non trouvé avec id", moduleId);
        return;
    }

    const module = this.state.modules[moduleIndex];
    const quizGranuleId = `final-quiz-${moduleId}`;

    // ✅ Ajout du granule uniquement si le quiz est réussi
    if (passed) {
        this.state.completedGranules.add(quizGranuleId);
        this.state.pesaCount += 10;

        // Déverrouiller le module suivant
        if (moduleIndex < this.state.modules.length - 1) {
            this.state.currentModuleIndex = moduleIndex + 1;
            this.state.modules[this.state.currentModuleIndex].isLocked = false;
        }
    }

    // Mémoriser que le quiz final a été passé (réussi ou non)
    module.finalQuizPassed = passed;
    module.finalQuizAttempted = true;

    // Mise à jour de la localisation
    this.state.currentLocation = {
        moduleId: this.state.modules[this.state.currentModuleIndex].id,
        granuleId: null
    };

    this._recalculateProgress();
    this._notifyStore();
  }

  isCapstoneCompleted() {
    const capstoneMod = this.state.modules.find(m => m.isCapstone);
    return capstoneMod?.granules?.every(g => this.state.completedGranules.has(g.id));
  }

  computeGlobalProgress() {
    const totalModules = this.state.modules.length;
    if (totalModules === 0) return 0;

    // Calcul basé sur la complétude de chaque module
    const sumCompletion = this.state.modules.reduce((acc, m) => acc + (m.completion || 0), 0);
    const globalProgress = Math.round(sumCompletion / totalModules);

    // Détermination du statut global
    let status = 'in progress';
    if (this.state.isCertified) status = 'completed';
    
    return {
      percent: globalProgress,
      status: status,
      totalPesa: this.state.pesaCount
    };
  }

  getModuleById(moduleId) {
    return this.state.modules.find(m => m.id === moduleId) || null;
  }

  _unlockNextModule() {
    const nextIdx = this.state.currentModuleIndex + 1;
    if (this.state.modules[nextIdx]) {
      this.state.modules[nextIdx].isLocked = false;
      this.state.modules[nextIdx].status = 'active';
    }
  }

  canSubmitToMentor() {
    const data = this.harvestedData;
    const modules = this.state.modules;

    // 1. Vérification de la complétude quantitative
    const reflectionCount = Object.keys(data.reflections).length;
    if (reflectionCount < modules.length) return false;

    // 2. Vérification de la "tenue" des preuves (No-IA)
    const hasValidEvidences = data.evidences.every(e => e.fileSize > 1024); // Ex: PDF > 1ko

    // 3. Score de cohérence
    const averageScore = modules.reduce((acc, m) => acc + m.score, 0) / modules.length;

    return hasValidEvidences && averageScore >= 70;
  }

  _notifyStore() {
    const store = getParcoursStore();
    if (!store) return;

    store.setState({
      modules: [...this.state.modules],
      currentModuleIndex: this.state.currentModuleIndex,
      completedGranules: new Set(this.state.completedGranules),
      pesaCount: this.state.pesaCount,
      isCertified: this.state.isCertified,
      harvestedData: { ...this.harvestedData }
    });
  }

  isLastModule() {
    return this.state.currentModuleIndex === this.course.modules.length - 1;
  }
}